import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { debounce } from "lodash"; // Necesitarás instalar lodash: npm install lodash
import axios from "axios"; // Importa axios

const fetchProductsApi = async (searchTerm = "", page = 0, pageSize = 10) => {
  try {
    const response = await axios.get(
      `/api/products?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}`
    );
    return response.data.products;
  } catch (error) {
    console.error("Error al obtener productos de la API:", error);
    throw new Error(
      "No se pudieron cargar los productos. Error de red o del servidor."
    );
  }
};

function DynamicProductSearchAutocomplete({ onProductSelect, resetKey }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [selectedValue, setSelectedValue] = useState(null);

  // Los parámetros 'page' y 'pageSize' pueden ser manejados si tu Autocomplete
  // necesita paginación en el futuro, pero para una búsqueda "type-ahead"
  // el enfoque es más en el searchTerm. Para este caso, los mantenemos fijos.
  const currentPage = 0; // Siempre la primera página para búsquedas rápidas
  const currentPageSize = 10; // Puedes ajustar el tamaño de página que tu API soporta para Autocomplete

  // Función debounced para llamar a la API
  const debouncedFetchProducts = useCallback(
    debounce(async (searchTerm) => {
      // No hacemos una llamada a la API si el término de búsqueda está vacío
      // y tampoco tenemos un valor seleccionado (para evitar búsquedas inútiles al inicio).
      if (!searchTerm && !selectedValue) {
        setOptions([]);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null); // Limpiar errores previos
      try {
        // Llama a la API real con el término de búsqueda
        const data = await fetchProductsApi(
          searchTerm,
          currentPage,
          currentPageSize
        );
        setOptions(data);
      } catch (err) {
        // El error ya es un objeto Error que relanzamos de fetchProductsApi
        console.error("Error capturado en Autocomplete:", err);
        setError(err.message || "Error desconocido al cargar productos.");
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 500), // Retraso de 500ms antes de llamar a la API
    [selectedValue] // Si selectedValue cambia, la función podría necesitar recrearse (aunque para debounce no es crítico)
  );

  useEffect(() => {
    setInputValue("");
    setSelectedValue(null);
    setOptions([]);
    setError(null);
    setLoading(false);
  }, [resetKey]);

  useEffect(() => {
    // Cuando el componente se monta, o inputValue/open cambian, o selectedValue cambia.
    // El objetivo es buscar si hay un inputValue o si el componente se abre
    // y no hay un selectedValue para precargar opciones (opcional).
    if (open || inputValue || selectedValue) {
      debouncedFetchProducts(inputValue);
    } else {
      // Si no está abierto, input vacío y nada seleccionado, limpiamos las opciones
      setOptions([]);
    }

    onProductSelect && onProductSelect(selectedValue);

    // Función de limpieza para cancelar cualquier llamada debounced pendiente
    return () => {
      debouncedFetchProducts.cancel();
    };
  }, [inputValue, open, selectedValue, debouncedFetchProducts]);

  return (
    <Box sx={{ width: 300 }}>
      <Autocomplete
        id="dynamic-product-search-autocomplete"
        open={open}
        onOpen={() => {
          setOpen(true);
          // Opcional: Si quieres recargar las opciones al abrir si el input está vacío
          // y no hay nada seleccionado, puedes hacer:
          // if (!inputValue && !selectedValue) debouncedFetchProducts('');
        }}
        onClose={() => setOpen(false)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.name || ""}
        options={options}
        loading={loading}
        // Cuando el usuario selecciona una opción de la lista
        onChange={(event, newValue) => {
          setSelectedValue(newValue);
          setInputValue(newValue ? newValue.name : ""); // Actualiza el texto del input
        }}
        // Cuando el usuario escribe en el campo de texto
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
          // Si el usuario borra todo el texto, y no hay un valor seleccionado,
          // aseguramos que selectedValue se limpie
          if (!newInputValue && !selectedValue) {
            setSelectedValue(null);
          }
        }}
        value={selectedValue} // Objeto de producto seleccionado
        inputValue={inputValue} // Texto actual en el input
        renderInput={(params) => (
          <TextField
            {...params}
            label="Buscar producto..."
            variant="outlined"
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        // Texto a mostrar si no hay opciones
        noOptionsText={
          loading
            ? "Cargando..."
            : error
            ? "Error al cargar productos"
            : "No hay productos que coincidan"
        }
      />
    </Box>
  );
}

export default DynamicProductSearchAutocomplete;
